import { Injectable } from '@angular/core';
import { fromEvent, Observable, race } from 'rxjs';
import { take } from 'rxjs/operators';
import { Point } from '../models/point';
import { Element } from '../../equipment/models/elements/element';
import { EquipmentBase } from '../../planogram/models/equipment-base';
import { ProductErrors, ProductGroup } from '../../planogram/models/product-group';
import { Block } from '../../planogram/models/block';
import { Rectangle } from '../../equipment/models/figures/rectangle';
import { Cylinder } from '../../equipment/models/figures/cylinder';
import { Utils } from './utils.service';
import { konvaSettings } from '../../equipment/models/konvaSettings';
import Konva from 'konva';
import * as jsts from 'jsts/dist/jsts';
import { EMPTY_PRODUCT_IMAGE_PATH } from './products.service';
import { SessionService } from './session.service';
import { GlobalSettings } from '../models/globalSettings';
import { ElementDrawerFactory, IElementDrawer, ITooltipLabels } from '../../equipment/models/elements/elementDrawer';
import { Equipment } from '../../equipment/models/equipment';
import { EquipmentElements } from '../../equipment/models/elements/equipmentElements';
import { RectangleSliced } from '../../equipment/models/figures/rectangle-sliced';
import { Figure } from '../../equipment/models/figures/figure';
import { BlockFontSizeType, PersonalSettings } from '../models/personalSettings';
import { Bounding } from '../models/bounding';
import { IOffsetLine } from '../../equipment/models/models';
import { UnitConverter } from '../utils/unitConverter';
import { POSTextMaterial } from '../../planogram/models/POSMaterials/POSTextMaterial';
import { POSMaterial } from '../../planogram/models/POSMaterials/POSMaterial';
import { POSImageMaterial } from '../../planogram/models/POSMaterials/POSImageMaterial';
import { KonvaEventObject } from 'konva/lib/Node';

const GEOMETRY: jsts.geom.GeometryFactory = new jsts.geom.GeometryFactory(),
	MAX_SCALE_VALUE: number = 14,
	STROKE_ACTIVE: number = 0.25,
	STROKE_INACTIVE: number = 1,
	STROKE_SELECTED: number = 1;

export type canvasProductParams = {
	productGroup: ProductGroup;
	stand: EquipmentBase;
	beginPoint: Point;
	scale: number;
	normalize?: Point;
	articleMode?: boolean;
	sequenceNumberMode?: boolean;
	flipMode?: boolean;
	dataObject?: any;
	labelsDictionary?: any;
	showStockQty?: boolean;
	showPrices?: boolean;
	showAbcTurnover?: boolean;
	showAbcReceipts?: boolean;
	showFallbackArticle?: boolean;
	zoom?: number;
	callback?: () => void;
}

export type canvasBlockParams = {
	block: Block;
	detailedElement?: Element;
	beginPoint: Point;
	scale: number;
	normalize?: Point;
	active: boolean;
	selected: boolean;
	draggable: boolean;
	zoom: number;
	flip?: boolean;
}

export type canvasPOSMaterialParams = {
	material: POSMaterial;
	beginPoint: Point;
	scale: number;
	active: boolean;
	draggable: boolean;
	zoom: number;
	canvasElement: HTMLElement;
	tooltipGroup: Konva.Group
}

export type canvasElementParams = {
	element: Element;
	beginPoint: Point;
	scale: number;
	zoom?: number;
	normalize?: Point;
	active: boolean;
	flip?: boolean;
	showLabels?: boolean;
}

@Injectable()
export class CanvasDrawerService {

	private _productGroups: WeakMap<ProductGroup, Konva.Group> = new WeakMap<ProductGroup, Konva.Group>();
	private _productGroupsTooltips: WeakMap<ProductGroup, Konva.Label> = new WeakMap<ProductGroup, Konva.Label>();
	private _POSMaterials: WeakMap<POSMaterial, Konva.Group> = new WeakMap<POSMaterial, Konva.Group>();
	private _blocks: WeakMap<Block, Konva.Group> = new WeakMap<Block, Konva.Group>();
	private _blocksDescriptionsTooltips: WeakMap<Block, Konva.Label> = new WeakMap<Block, Konva.Label>();
	private _blocksTooltips: WeakMap<Block, Konva.Label> = new WeakMap<Block, Konva.Label>();
	private _equipmentElements: WeakMap<Element, Konva.Group> = new WeakMap<Element, Konva.Group>();
	private _equipmentElementTooltips: WeakMap<Element, Konva.Label> = new WeakMap<Element, Konva.Label>();
	private _defaultLayer: Konva.Layer;
	protected _defaultGroup: Konva.Group;

	constructor(protected sessionService: SessionService) {
		this._defaultLayer = new Konva.Layer();
		this._defaultGroup = new Konva.Group();
		this._defaultLayer.add(this._defaultGroup);

	}

	protected isProductImageFrameEnabled(pg: ProductGroup): boolean {
		const displayProductImageFrame: boolean = this.sessionService.personalSettings.displayProductImageFrame;
		const displayInactiveProductImageFrame: boolean = this.sessionService.personalSettings.displayInactiveProductImageFrame;

		return pg.inactive
			? (displayInactiveProductImageFrame || displayProductImageFrame)
			: displayProductImageFrame;
	}

	protected useInactiveProductBlackFrame(pg: ProductGroup): boolean {
		return pg.inactive && this.sessionService.personalSettings.displayInactiveProductImageFrame;
	}

	protected getProductStrokeWidth(pg: ProductGroup): number {
		return this.useInactiveProductBlackFrame(pg) ? STROKE_INACTIVE : STROKE_ACTIVE;
	}

	protected getProductStrokeColor(pg: ProductGroup): string {
		return this.useInactiveProductBlackFrame(pg)
			? konvaSettings.inactiveProductColor
			: konvaSettings.fillDarkColor;
	}

	protected drawProductFace(
		params: canvasProductParams,
		canvasProduct: Konva.Group,
		faceX: number,
		faceY: number,
		faceWidth: number,
		faceHeight: number
	): void {
		const pg: ProductGroup = params.productGroup;
		const strokeWidth: number = this.getProductStrokeWidth(pg);
		const transparentProductBackground: boolean = this.sessionService.personalSettings.transparentProductImagesBackground;

		const masterPositionDash: Array<number> = pg.product.is_master_position ? [5, 5] : [];

		if (pg.image?.img?.complete && !pg.error && !params.articleMode && !ProductGroup.imageNotFound(pg.image.img.src)) {

			const packingOffsetPercent: number = this.sessionService.personalSettings.packingOffset
					? GlobalSettings.PACKING_OFFSET_PERCENT
					: 0,
				xBorder: number = pg.selectPacking ? faceWidth * packingOffsetPercent : 0,
				yBorder: number = pg.selectPacking ? faceHeight * packingOffsetPercent : 0,
				frameOutsidePackingOffset: number = this.sessionService.personalSettings.packingOffset && pg.selectPacking,
				hasProductImageFrame: boolean = this.isProductImageFrameEnabled(pg) || !pg.selectPacking
					|| Boolean(frameOutsidePackingOffset),
				imageBoxW: number = (pg.image.rotation ? faceHeight : faceWidth) - strokeWidth * 2,
				imageBoxH: number = (pg.image.rotation ? faceWidth : faceHeight) - strokeWidth * 2,
				image: Konva.Image = new Konva.Image({
					image: pg.image.img,
					name: pg.selectPacking ? 'packed-image' : 'image',
					offsetY: pg.image.rotation === 90 ? (faceWidth - strokeWidth * 2) : 0,
					offsetX: pg.image.rotation === -90 ? (faceHeight - strokeWidth * 2) : 0,
					rotation: pg.image.rotation,
					x: faceX + xBorder + strokeWidth,
					y: faceY + yBorder + strokeWidth,
					width: imageBoxW,
					height: imageBoxH
				});

			if (!pg.selectPacking) {
				const faceRectConfig: Konva.RectConfig = {
					name: 'face',
					x: faceX + strokeWidth / 2,
					y: faceY + strokeWidth / 2,
					width: faceWidth - strokeWidth,
					height: faceHeight - strokeWidth
				};
				if (!transparentProductBackground) {
					faceRectConfig.fill = 'white';
				}
				canvasProduct.add(new Konva.Rect(faceRectConfig));
			}

			if (pg.selectPacking) {
				image.scale({ x: 1 - packingOffsetPercent * 2, y: 1 - packingOffsetPercent * 2 });
				const packingFaceRectConfig: Konva.RectConfig = {
					name: 'face',
					x: faceX + strokeWidth / 2,
					y: faceY + strokeWidth / 2,
					width: faceWidth - strokeWidth,
					height: faceHeight - strokeWidth
				};

				if (!transparentProductBackground) {
					packingFaceRectConfig.fill = 'white';
				}
				canvasProduct.add(new Konva.Rect(packingFaceRectConfig));
			}

			canvasProduct.add(image);

			if (hasProductImageFrame) {
				const frameStrokeW: number = (this.useInactiveProductBlackFrame(pg) ? strokeWidth : STROKE_ACTIVE) * 2,
					frameInsetX: number = frameOutsidePackingOffset ? 0 : xBorder,
					frameInsetY: number = frameOutsidePackingOffset ? 0 : yBorder,
					imageFrame: Konva.Rect = new Konva.Rect({
						name: 'image-frame',
						x: faceX + frameInsetX + strokeWidth,
						y: faceY + frameInsetY + strokeWidth,
						width: imageBoxW,
						height: imageBoxH,
						rotation: pg.image.rotation,
						offsetX: pg.image.rotation === -90 ? imageBoxW : 0,
						offsetY: pg.image.rotation === 90 ? imageBoxH : 0,
						fillEnabled: false,
						stroke: this.getProductStrokeColor(pg),
						strokeWidth: frameStrokeW,
						strokeEnabled: this.isProductImageFrameEnabled(pg),
						listening: false
					});
				if (pg.product.is_master_position) imageFrame.dash(masterPositionDash);
				canvasProduct.add(imageFrame);
			}
		} else {
			const faceStrokeW: number = strokeWidth * 2;
			const faceRectConfig: Konva.RectConfig = {
				fill: pg.error ? konvaSettings.errorColor : konvaSettings.fillGreyColor,
				name: 'face',
				x: faceX + faceStrokeW / 2,
				y: faceY + faceStrokeW / 2,
				width: faceWidth - faceStrokeW,
				height: faceHeight - faceStrokeW,
			};
			faceRectConfig.dash = masterPositionDash;
			faceRectConfig.strokeWidth = faceStrokeW;
			faceRectConfig.stroke = this.getProductStrokeColor(pg);

			canvasProduct.add(new Konva.Rect(faceRectConfig));
		}

		if (params.dataObject.hasOwnProperty('color')) {
			canvasProduct.add(new Konva.Rect({
				fill: pg.error ? konvaSettings.errorColor : params.dataObject.color,
				name: 'analytics',
				opacity: 0.5,
				x: faceX,
				y: faceY,
				width: faceWidth,
				height: faceHeight
			}));
		}

		if (pg.element.parent.is(EquipmentElements.HangBar)) {
			const arcRadius: number = (pg.element.shapes[0] as Cylinder).diameter / 2 * params.scale;
			const hangerGroup: Konva.Group = new Konva.Group({
				name: 'hanger',
				x: faceX,
				y: faceY - 0.08 * params.scale,
				width: faceWidth,
				height: 0.08 * params.scale
			});

			hangerGroup.add(new Konva.Arc({
				x: hangerGroup.width() / 2,
				y: -arcRadius,
				innerRadius: arcRadius,
				outerRadius: arcRadius + 2,
				angle: 180,
				rotation: -90,
				fill: konvaSettings.fillDarkColor
			}));
			hangerGroup.add(new Konva.Rect({
				x: hangerGroup.width() / 2 - 0.001 * params.scale,
				y: 0,
				width: 0.002 * params.scale,
				height: hangerGroup.height(),
				fill: konvaSettings.fillDarkColor
			}));
			hangerGroup.add(new Konva.Rect({
				x: hangerGroup.width() / 4,
				y: 0.06 * params.scale,
				width: hangerGroup.width() / 2,
				height: 0.02 * params.scale,
				fill: konvaSettings.fillDarkColor
			}));

			canvasProduct.add(hangerGroup);
		}

		if (pg.element.depend?.parent.is(EquipmentElements.HangBar)) {
			const arcRadius: number = pg.element.shapes[0].height / 2 * params.scale;
			const hangerGroup: Konva.Group = new Konva.Group({
				name: 'hanger',
				x: faceX,
				y: faceY - 0.08 * params.scale,
				width: faceWidth,
				height: 0.08 * params.scale
			});

			hangerGroup.add(new Konva.Arc({
				x: hangerGroup.width() / 2,
				y: -arcRadius,
				innerRadius: arcRadius,
				outerRadius: arcRadius + 2,
				angle: 270,
				rotation: -180,
				fill: konvaSettings.fillDarkColor
			}));
			hangerGroup.add(new Konva.Rect({
				x: hangerGroup.width() / 2 - 0.001 * params.scale,
				y: 0,
				width: 0.002 * params.scale,
				height: hangerGroup.height(),
				fill: konvaSettings.fillDarkColor
			}));
			hangerGroup.add(new Konva.Rect({
				x: 0,
				y: 0.06 * params.scale,
				width: faceWidth,
				height: 0.02 * params.scale,
				fill: konvaSettings.fillDarkColor
			}));

			canvasProduct.add(hangerGroup);
		}
	}

	protected createCanvasTooltip(konvaEl: Konva.Node, txt: string, canvasElement: HTMLElement): Konva.Label {
		const maxTooltipWidth: number = canvasElement.offsetWidth / 2;
		const tooltip: Konva.Label = new Konva.Label({
			name: 'tooltip',
			visible: false,
			opacity: 0.9
		});
		const tooltipTag: Konva.Tag = new Konva.Tag({
			id: 'tooltip-tag',
			fill: 'white',
			pointerDirection: 'down',
			pointerWidth: 10,
			pointerHeight: 10,
			lineJoin: 'round',
			shadowColor: 'black',
			shadowBlur: 10,
			shadowOpacity: 0.5
		});
		const tooltipText: Konva.Text = new Konva.Text({
			id: 'tooltip-text',
			fill: 'black',
			text: txt,
			fontSize: 12,
			padding: 5,
			wrap: 'word'
		});

		if (tooltipText?.width() > maxTooltipWidth) tooltipText.setAttr('width', maxTooltipWidth);

		const transform = konvaEl.getAbsoluteTransform();
		const corners = [
			transform.point({ x: 0, y: 0 }),
			transform.point({ x: konvaEl.width(), y: 0 }),
			transform.point({ x: konvaEl.width(), y: konvaEl.height() }),
			transform.point({ x: 0, y: konvaEl.height() })
		];
		corners.forEach((corner: Point) => {
			corner.x = Utils.round(corner.x);
			corner.y = Utils.round(corner.y);
		});

		let minYPoints = corners.filter((corner: Point) => corner.y === Math.min(...corners.map(c => c.y)));
		let maxYPoints = corners.filter((corner: Point) => corner.y === Math.max(...corners.map(c => c.y)));
		let minXPoints = corners.filter((corner: Point) => corner.x === Math.min(...corners.map(c => c.x)));
		let maxXPoints = corners.filter((corner: Point) => corner.x === Math.max(...corners.map(c => c.x)));

		if (minYPoints.length === 2 && minXPoints.length === 2) {
			minYPoints = [{ x: Utils.round((minYPoints[0].x + minYPoints[1]?.x) / 2), y: minYPoints[0].y }];
			maxYPoints = [{ x: Utils.round((maxYPoints[0].x + maxYPoints[1]?.x) / 2), y: maxYPoints[0].y }];
			minXPoints = [{ x: minXPoints[0].x, y: Utils.round((minXPoints[0].y + minXPoints[1]?.y) / 2) }];
			maxXPoints = [{ x: maxXPoints[0].x, y: Utils.round((maxXPoints[0].y + maxXPoints[1]?.y) / 2) }];
		}

		tooltip.add(tooltipTag);
		tooltip.add(tooltipText);
		tooltip.setAttrs(minYPoints[0]);

		if (tooltip.y() - tooltip.height() - 10 < 0) {
			tooltip.setAttrs({ x: maxYPoints[0].x, y: maxYPoints[0].y });
			tooltipTag.pointerDirection('up');
		}

		if (tooltip.y() + tooltip.height() - 10 > canvasElement.offsetHeight || tooltip.x() - tooltip.width() / 2 < 0) {
			tooltip.setAttrs({ x: maxXPoints[0].x, y: maxXPoints[0].y });
			tooltipTag.pointerDirection('left');
		}

		if (tooltip.x() + tooltip.width() / 2 > canvasElement.offsetWidth) {
			tooltip.setAttrs({ x: minXPoints[0].x, y: minXPoints[0].y });
			tooltipTag.pointerDirection('right');
		}

		return tooltip;
	}

	drawProduct(params: canvasProductParams, canvasProduct: Konva.Group): void {
		canvasProduct
			.find('.face, .packed-image, .image, .image-frame, .analytics, .product-side, .face-eclipse')
			.forEach((node: Konva.Node) => node.destroy());

		const pg: ProductGroup = params.productGroup;

		const drawProductFaces = (placementWidth: number, placementHeight: number, element: Konva.Group) => {
			for (let i = 0; i < placementWidth; i++) {
				const faceWidth: number = Utils.round(element.width() / placementWidth);
				const xPoint: number = Utils.round(i * faceWidth);

				for (let j = 1; j <= placementHeight; j++) {
					const faceHeight: number = Utils.round(element.height() / placementHeight);
					const yPoint: number = Utils.round(j * faceHeight - faceHeight);

					this.drawProductFace(params, element, xPoint, yPoint, faceWidth, faceHeight);
				}
			}
		}

		for (let side of pg.frontVisibleSides) {
			const sideElement: Konva.Group = new Konva.Group({
				name: 'product-side',
				x: side.xMin * params.scale,
				y: 0,
				height: canvasProduct.height(),
				width: side.width * params.scale
			});

			if (['front', 'back'].includes(side.side)) {
				drawProductFaces(pg.FacesQty, pg.getPlacementHeight, sideElement);
			} else {
				drawProductFaces(pg.getPlacementDepth, pg.getPlacementHeight, sideElement);

				sideElement.find('.face, .packed-image, .image, .analytics').forEach((node: Konva.Node) => {
					sideElement.add(new Konva.Rect({
						name: 'face-eclipse',
						width: node.width(),
						height: node.height(),
						x: node.x(),
						y: node.y(),
						fill: 'black',
						opacity: 0.5,
						rotation: node.rotation(),
						offsetX: node.offsetX(),
						offsetY: node.offsetY()
					}));
				})
			}

			canvasProduct.add(sideElement);
		}
	}

	drawWeightProduct(params: canvasProductParams, canvasProduct: Konva.Group): void {
		canvasProduct.find('.face, .packed-image, .image, .image-frame, .analytics, .product-side, .face-eclipse').forEach((node: Konva.Node) => node.destroy());

		this.drawProductFace(params, canvasProduct, 0, 0, canvasProduct.width(), canvasProduct.height());
	}

	drawArticle(params: canvasProductParams, canvasProduct: Konva.Group, detailedView: boolean = false): void {
		canvasProduct.find('.article').forEach((node: Konva.Node) => node.destroy());

		let pg: ProductGroup = params.productGroup,
			yValue: number = detailedView ? pg.Depth : pg.Height,
			rotation: boolean = yValue > pg.Width,
			width: number = rotation ? yValue : pg.Width,
			height: number = rotation ? pg.Width : yValue;

		if (detailedView && pg.isLooseForm) {
			width = pg.element.Width;
			height = pg.element.Depth;
		}

		if (detailedView && pg.element.parent.type == 'Hook') {
			rotation = pg.Height > pg.Depth;
			width = rotation ? pg.Height : pg.Depth;
			height = rotation ? pg.Depth : pg.Height;
		}

		const article: Konva.Text = new Konva.Text({ text: pg.product.article, x: 3, y: 3 });

		if (article.getWidth() > width * params.scale - 10) article.setAttrs({ width: width * params.scale - 10 });

		const articleRect: Konva.Rect = new Konva.Rect({
				fill: pg.error ? konvaSettings.errorColor : konvaSettings.fillGreyColor,
				width: article.getWidth() + 6,
				height: article.getHeight() + 6
			}),
			articleGroup: Konva.Group = new Konva.Group({
				id: String(pg.product.id),
				rotation: (rotation) ? -90 : 0,
				name: 'article'
			});

		if (articleRect.height() + 4 > height * params.scale) {
			article.fontSize(8);
			article.setAttrs({ x: 1, y: 1 });
			articleRect.setAttrs({ width: article.getWidth() + 2, height: article.getHeight() + 2 });
		}

		articleGroup.add(articleRect);
		articleGroup.add(article);

		articleGroup.setAttrs({
			x: (canvasProduct.width() - articleRect.width()) / 2,
			y: (canvasProduct.height() - articleRect.height()) / 2,
			width: articleRect.width(),
			height: articleRect.height()
		});

		if (rotation) {
			articleGroup.setAttrs({
				x: (canvasProduct.width() - articleRect.height()) / 2,
				y: (canvasProduct.height() + articleRect.width()) / 2
			});
		}

		canvasProduct.add(articleGroup);
	}

	drawProductNumber(params: canvasProductParams, canvasProduct: Konva.Group, idx: number = null) {
		canvasProduct.find('.number-label').forEach((node: Konva.Node) => node.destroy());

		const pg: ProductGroup = params.productGroup,
			labelText: Konva.Text = new Konva.Text({
				text: String(idx || pg.number),
				fontSize: 12,
				x: 3,
				y: 2
			}),
			labelRect: Konva.Rect = new Konva.Rect({
				fill: 'white',
				stroke: 'black',
				strokeWidth: 1,
				width: labelText.getWidth() + 6,
				height: labelText.getHeight() + 4
			}),
			labelGroup: Konva.Group = new Konva.Group({
				name: 'number-label',
				x: canvasProduct.width() - labelRect.width() - 2,
				y: canvasProduct.height() - labelRect.height() - 2,
				width: labelRect.width(),
				height: labelRect.height()
			});

		labelGroup.add(labelRect);
		labelGroup.add(labelText);
		canvasProduct.add(labelGroup);
	}

	drawTextLabel(params: canvasProductParams, canvasProduct: Konva.Group, name: string, text: string, fill: string, rotated: boolean = false): Konva.Group {
		canvasProduct.find(`.${name}`).forEach((node: Konva.Node) => node.destroy());

		const labelText = new Konva.Text({
			text: text,
			align: 'center',
			fontSize: Math.min((params.zoom - 100) / 10 + 9.5, MAX_SCALE_VALUE),
			x: 3,
			y: 3
		});
		const labelRect = new Konva.Rect({
			fill: fill,
			stroke: 'black',
			strokeWidth: 1,
			width: labelText.getWidth() + 6,
			height: labelText.getHeight() + 6
		});
		const labelGroup = new Konva.Group({
			name: name,
			width: labelRect.width(),
			height: labelRect.height()
		});

		if (rotated) {
			labelText.rotation(180);
			labelText.offsetX(labelText.width());
			labelText.offsetY(labelText.height());
		}

		labelGroup.add(labelRect);
		labelGroup.add(labelText);
		canvasProduct.add(labelGroup);

		return labelGroup;
	}

	drawMarkers(params: canvasProductParams, canvasProduct: Konva.Group, frontView: boolean = true): void {
		let pg: ProductGroup = params.productGroup,
			delta: number = (['Box', 'SlicedBox'].includes(pg.element.parent.type) && pg.isLooseForm && frontView)
				? pg.element.shapes[0].wallWidth * params.scale
				: 0,
			beginPointX: number = canvasProduct.width() / 2,
			beginPointY: number = canvasProduct.height() + delta;

		const rotated: boolean = !frontView && pg.angle > 90 && pg.angle < 270;

		if (params.showStockQty) {
			const label: Konva.Group = this.drawTextLabel(
				params,
				canvasProduct,
				'stock',
				String(pg.product.stock_qty ?? '-'),
				'white',
				rotated
			);

			label.setAttrs({
				x: beginPointX - label.width() / 2,
				y: beginPointY
			});

			beginPointY += label.height();
		}

		if (params.showPrices) {
			const label: Konva.Group = this.drawTextLabel(
				params,
				canvasProduct,
				'price',
				pg.price || '0',
				'white'
			);

			label.setAttrs({
				x: beginPointX - label.width() / 2,
				y: beginPointY
			});

			beginPointY += label.height();
		}

		if (params.showAbcTurnover && pg.product.abc_turnover) {
			const label: Konva.Group = this.drawTextLabel(
				params,
				canvasProduct,
				'turnover',
				String(pg.product.abc_turnover),
				'white',
				rotated
			);

			beginPointX = params.showAbcReceipts ? beginPointX - label.width() / 2 : beginPointX;

			label.setAttrs({
				x: beginPointX - label.width() / 2,
				y: beginPointY
			});

			beginPointX += label.width();
		}

		if (params.showAbcReceipts && pg.product.abc_receipts) {
			const label: Konva.Group = this.drawTextLabel(
				params,
				canvasProduct,
				'receipts',
				String(pg.product.abc_receipts),
				'white',
				rotated
			);

			label.setAttrs({
				x: beginPointX - label.width() / 2,
				y: beginPointY
			});
		}
	}

	loadImage(image: HTMLImageElement): Promise<void> {
		return new Promise((resolve) => {
			if (image.complete) return resolve();

			const imageLoad$: Observable<Event> = fromEvent(image, 'load'),
				imageError$: Observable<Event> = fromEvent(image, 'error');

			race(imageLoad$, imageError$)
				.pipe(take(1))
				.subscribe((value: Event) => {
					if (value.type === 'error') image.src = EMPTY_PRODUCT_IMAGE_PATH;
					resolve();
				});
		});
	}

	createCanvasProduct(params: canvasProductParams): Konva.Group {
		const pg: ProductGroup = params.productGroup,
			canvasProduct: Konva.Group = new Konva.Group({ name: 'productGroup inactive' });
		const image = pg.image?.img;

		params.beginPoint = Object.assign({}, params.beginPoint);

		if (pg.isLooseForm) {
			const shape = pg.element.shapes[0];

			let x: number = pg.element.getBoundingX().min,
				y: number = params.stand.Height - pg.begin.y - pg.Height;

			if (params.flipMode) x = params.stand.Width - pg.element.getBoundingX().max;

			if (['Box', 'SlicedBox'].includes(pg.element.parent.type)) {
				if (['AngleShelf'].includes(pg.element.depend.parent.type)) {
					y = params.stand.Height - pg.element.getBoundingY().min - pg.Height;
				}

				x += shape.wallWidth;
			}

			if (['Hook', 'Hole'].includes(pg.element.parent.type)) {
				x = pg.begin.x;
			}

			canvasProduct.setAttrs({
				id: String(pg.element.id),
				x: Utils.round(x * params.scale + konvaSettings.canvasOffset + params.beginPoint.x),
				y: Utils.round(y * params.scale + konvaSettings.canvasOffset + params.beginPoint.y),
				width: Utils.round(pg.Width * params.scale),
				height: Utils.round(pg.Height * params.scale)
			});
			this.drawWeightProduct(params, canvasProduct);
		} else {
			let begin: Point = { x: pg.getBoundingX().min, y: pg.begin.y, z: pg.getBoundingZ().max };
			let end: Point = {
				x: pg.getBoundingX().max,
				y: Utils.round(pg.begin.y + pg.Height),
				z: pg.getBoundingZ().min
			};

			if (params.flipMode) {
				begin.x = params.stand.Width - pg.getBoundingX().max;
				begin.z = params.stand.Depth - pg.getBoundingZ().min;
				end.x = params.stand.Width - pg.getBoundingX().min;
				end.z = params.stand.Depth - pg.getBoundingZ().max;
			}

			if (
				pg.element.shapes[0].type != 'trapeze'
				&& (pg.element.shapes[0] as Rectangle | RectangleSliced).frontAngle
			) {
				begin = (pg.element.shapes[0] as Rectangle | RectangleSliced).rotatePointByFrontAngle(begin);
				end = (pg.element.shapes[0] as Rectangle | RectangleSliced).rotatePointByFrontAngle(end);
			}

			canvasProduct.setAttrs({
				id: String(pg.element.id),
				x: Utils.round(begin.x * params.scale + konvaSettings.canvasOffset + params.beginPoint.x),
				y: Utils.round((params.stand.Height - end.y) * params.scale + konvaSettings.canvasOffset + params.beginPoint.y),
				width: Utils.round((end.x - begin.x) * params.scale),
				height: Utils.round((end.y - begin.y) * params.scale)
			});
			this.drawProduct(params, canvasProduct);
		}

		const shouldDrawArticle: boolean = params.showFallbackArticle !== false &&
			(!image?.complete || pg.error || params.articleMode || ProductGroup.imageNotFound(image?.src));

		if (shouldDrawArticle) {
			this.drawArticle(params, canvasProduct, false);
		}

		if (params.sequenceNumberMode) this.drawProductNumber(params, canvasProduct);

		if (image && !image.complete) {
			this.loadImage(image).then(() => {
				this.drawProduct(params, canvasProduct);

				if (params.showFallbackArticle !== false &&
					(pg.error || params.articleMode || ProductGroup.imageNotFound(image?.src))
				) {
					this.drawArticle(params, canvasProduct, false);
				}

				if (params.sequenceNumberMode) this.drawProductNumber(params, canvasProduct);

				params.callback();
			});
		}

		if (params.showStockQty || params.showPrices || params.showAbcTurnover || params.showAbcReceipts) {
			const elBounding: Bounding = pg.element.getAbsoluteBoundingZ();
			const pgBounding: Bounding = pg.getBoundingZ();

			if (pgBounding.max == elBounding.max || (params.flipMode && pgBounding.min == elBounding.min) || pg.element.parent.is(EquipmentElements.Hole)) {
				this.drawMarkers(params, canvasProduct);
			}
		}

		if (this._productGroups.has(pg)) this._productGroups.get(pg)?.destroy();
		this._productGroups.set(pg, canvasProduct);
		return canvasProduct;
	}

	getCanvasProduct(pg: ProductGroup): Konva.Group {
		if (!this._productGroups.has(pg)) return this._defaultGroup;
		return this._productGroups.get(pg);
	}

	protected shouldSuppressProductFaceStroke(
		pg: ProductGroup,
		hasProductImageLayer: boolean,
		hasImageFrame: boolean = false
	): boolean {
		if (!hasProductImageLayer) return false;
		const displayProductImageFrame: boolean = this.sessionService.personalSettings.displayProductImageFrame;
		if (pg.selectPacking) {
			return hasImageFrame;
		}
		return (
			(pg.inactive && this.isProductImageFrameEnabled(pg)) ||
			(!pg.inactive && displayProductImageFrame && pg.product.is_master_position) ||
			(hasImageFrame && displayProductImageFrame && !pg.inactive)
		);
	}

	setCanvasProductActive(pg: ProductGroup, canvasProduct: Konva.Group): void {
		if (!canvasProduct.hasName('active')) {
			const strokeWidth: number = this.getProductStrokeWidth(pg);
			const strokeDiff: number = STROKE_SELECTED - strokeWidth;
			const rasterImageShapes = canvasProduct.find('.image, .packed-image');
			const imageFrameShapes = canvasProduct.find('.image-frame');
			const hasImageFrame: boolean = imageFrameShapes.length > 0;
			const packingImageFrameExpected: boolean = pg.selectPacking && this.isProductImageFrameEnabled(pg);
			const selectedFaceStrokeWidth: number = (
				pg.selectPacking && !hasImageFrame && !packingImageFrameExpected
			) ? STROKE_SELECTED * 2 : STROKE_SELECTED;
			const hasProductImageLayer: boolean = rasterImageShapes.length > 0;
			const baseFaceStrokeWidth: number = hasProductImageLayer ? strokeWidth : strokeWidth * 2;
			const selectedFaceStrokeWidthEffective: number = (
				!hasProductImageLayer && selectedFaceStrokeWidth === STROKE_SELECTED
			)
				? STROKE_SELECTED * 2
				: selectedFaceStrokeWidth;
			const faceStrokeDiff: number = selectedFaceStrokeWidthEffective - baseFaceStrokeWidth;
			const hideFaceStrokeWhileSelected: boolean = this.shouldSuppressProductFaceStroke(
				pg,
				hasProductImageLayer,
				hasImageFrame
			) || (!pg.selectPacking && hasImageFrame && !this.isProductImageFrameEnabled(pg));
			const masterPositionDash: Array<number> = pg.product.is_master_position ? [5, 5] : [];

			canvasProduct.find('.face').forEach((el: Konva.Shape) => {
				if (hideFaceStrokeWhileSelected) {
					el.strokeEnabled(false);
					return;
				}
				el.strokeEnabled(true);
				el.stroke(konvaSettings.activeColor);
				el.dash(masterPositionDash);
				if (faceStrokeDiff) {
					el.x(el.x() + faceStrokeDiff / 2);
					el.y(el.y() + faceStrokeDiff / 2);
					el.width(el.width() - faceStrokeDiff);
					el.height(el.height() - faceStrokeDiff);
					el.strokeWidth(selectedFaceStrokeWidthEffective);
				}
			});

			const applyImageLayerGeometry = (el: Konva.Shape): void => {
				if (strokeDiff) {
					if (pg.image.rotation === 90) el.offsetY(el.offsetY() - strokeDiff * 2);
					if (pg.image.rotation === -90) el.offsetX(el.offsetX() - strokeDiff * 2);
					el.x(el.x() + strokeDiff);
					el.y(el.y() + strokeDiff);
					el.width(el.width() - strokeDiff * 2);
					el.height(el.height() - strokeDiff * 2);
				}
			};
			rasterImageShapes.forEach((el: Konva.Shape) => applyImageLayerGeometry(el));
			imageFrameShapes.forEach((el: Konva.Shape) => applyImageLayerGeometry(el));

			const setSelectionStroke = (el: Konva.Shape): void => {
				el.strokeEnabled(true);
				el.stroke(konvaSettings.activeColor);
				if (strokeDiff) el.strokeWidth(STROKE_SELECTED * 2);
				el.dash(masterPositionDash);
			};
			if (hasImageFrame || packingImageFrameExpected) {
				rasterImageShapes.forEach((el: Konva.Shape) => el.strokeEnabled(false));
				imageFrameShapes.forEach((el: Konva.Shape) => setSelectionStroke(el));
			} else {
				if (pg.selectPacking) {
					rasterImageShapes.forEach((el: Konva.Shape) => el.strokeEnabled(false));
				} else {
					rasterImageShapes.forEach((el: Konva.Shape) => setSelectionStroke(el));
				}
			}

			canvasProduct.find('.product-rotation-circle')[0]?.visible(true);

			canvasProduct.removeName('inactive');
			canvasProduct.addName('active');
		}
	}

	setCanvasProductInactive(pg: ProductGroup, canvasProduct: Konva.Group): void {
		if (!canvasProduct.hasName('inactive')) {
			const strokeWidth: number = this.getProductStrokeWidth(pg);
			const strokeDiff: number = STROKE_SELECTED - strokeWidth;
			const rasterImageShapes = canvasProduct.find('.image, .packed-image');
			const imageFrameShapes = canvasProduct.find('.image-frame');
			const hasImageFrame: boolean = imageFrameShapes.length > 0;
			const packingImageFrameExpected: boolean = pg.selectPacking && this.isProductImageFrameEnabled(pg);
			const selectedFaceStrokeWidth: number = (
				pg.selectPacking && !hasImageFrame && !packingImageFrameExpected
			) ? STROKE_SELECTED * 2 : STROKE_SELECTED;
			const hasProductImageLayer: boolean = rasterImageShapes.length > 0;
			const baseFaceStrokeWidth: number = hasProductImageLayer ? strokeWidth : strokeWidth * 2;
			const selectedFaceStrokeWidthEffective: number = (
				!hasProductImageLayer && selectedFaceStrokeWidth === STROKE_SELECTED
			)
				? STROKE_SELECTED * 2
				: selectedFaceStrokeWidth;
			const faceStrokeDiff: number = selectedFaceStrokeWidthEffective - baseFaceStrokeWidth;
			const shouldShowDefaultFaceStroke: boolean = !hasProductImageLayer
				|| (!pg.inactive && this.isProductImageFrameEnabled(pg));

			const masterPositionDash: Array<number> = pg.product.is_master_position ? [5, 5] : [];

			const faceStrokeOnlyOnFrame: boolean = this.shouldSuppressProductFaceStroke(
				pg,
				hasProductImageLayer,
				hasImageFrame
			);
			const hideFaceStrokeWhileSelected: boolean = faceStrokeOnlyOnFrame
				|| (!pg.selectPacking && hasImageFrame && !this.isProductImageFrameEnabled(pg));

			canvasProduct.find('.face').forEach((el: Konva.Shape) => {
				if (!hideFaceStrokeWhileSelected && faceStrokeDiff) {
					el.x(el.x() - faceStrokeDiff / 2);
					el.y(el.y() - faceStrokeDiff / 2);
					el.width(el.width() + faceStrokeDiff);
					el.height(el.height() + faceStrokeDiff);
					el.strokeWidth(baseFaceStrokeWidth);
				}

				if (faceStrokeOnlyOnFrame) {
					el.strokeEnabled(false);
					el.dash(masterPositionDash);
				} else if (this.useInactiveProductBlackFrame(pg)) {
					el.strokeEnabled(true);
					el.stroke(this.getProductStrokeColor(pg));
					el.dash(masterPositionDash);
				} else if (pg.inactive && !hasProductImageLayer) {
					el.strokeEnabled(true);
					el.stroke(this.getProductStrokeColor(pg));
					el.strokeWidth(baseFaceStrokeWidth);
					el.dash(masterPositionDash);
				} else if (shouldShowDefaultFaceStroke) {
					el.strokeEnabled(true);
					el.stroke(konvaSettings.fillDarkColor);
					el.strokeWidth(baseFaceStrokeWidth);
					el.dash(masterPositionDash);
				} else {
					el.strokeEnabled(false);
				}
			});

			const applyImageLayerGeometryInactive = (el: Konva.Shape): void => {
				if (strokeDiff) {
					if (pg.image.rotation === 90) el.offsetY(el.offsetY() + strokeDiff * 2);
					if (pg.image.rotation === -90) el.offsetX(el.offsetX() + strokeDiff * 2);
					el.x(el.x() - strokeDiff);
					el.y(el.y() - strokeDiff);
					el.width(el.width() + strokeDiff * 2);
					el.height(el.height() + strokeDiff * 2);
				}
			};
			rasterImageShapes.forEach((el: Konva.Shape) => applyImageLayerGeometryInactive(el));
			imageFrameShapes.forEach((el: Konva.Shape) => applyImageLayerGeometryInactive(el));

			const restoreFrameStroke = (el: Konva.Shape): void => {
				if (strokeDiff) el.strokeWidth(strokeWidth * 2);
				if (pg.inactive) {
					const showFrame: boolean = this.isProductImageFrameEnabled(pg);
					el.strokeEnabled(showFrame);
					if (showFrame) {
						el.stroke(this.getProductStrokeColor(pg));
						el.dash(masterPositionDash);
					}
				} else if (this.isProductImageFrameEnabled(pg)) {
					el.strokeEnabled(true);
					el.stroke(konvaSettings.fillDarkColor);
					el.dash(masterPositionDash);
				} else {
					el.strokeEnabled(false);
				}
			};
			if (hasImageFrame || packingImageFrameExpected) {
				rasterImageShapes.forEach((el: Konva.Shape) => el.strokeEnabled(false));
				imageFrameShapes.forEach((el: Konva.Shape) => restoreFrameStroke(el));
			} else {
				rasterImageShapes.forEach((el: Konva.Shape) => restoreFrameStroke(el));
			}

			canvasProduct.find('.product-rotation-circle')[0]?.visible(false);

			canvasProduct.removeName('active');
			canvasProduct.addName('inactive');
		}
	}

	createCanvasProductTooltip(pg: ProductGroup, text: string, canvasElement: HTMLElement): Konva.Label {
		const tooltip = this.createCanvasTooltip(this.getCanvasProduct(pg), text, canvasElement);
		tooltip.id(String(pg.element.id));
		if (this._productGroupsTooltips.has(pg)) this._productGroupsTooltips.get(pg)?.destroy();
		this._productGroupsTooltips.set(pg, tooltip);
		return tooltip;
	}

	getCanvasProductTooltip(pg: ProductGroup): Konva.Label {
		if (!this._productGroupsTooltips.has(pg)) return new Konva.Label();
		return this._productGroupsTooltips.get(pg);
	}

	createProductRotationCircle(element: Konva.Group, params: Partial<canvasProductParams>): Konva.Circle {
		return new Konva.Circle({
			name: 'product-rotation-circle',
			x: element.width(),
			y: this.sessionService.personalSettings.productRotationPoint === 'topLeft' ? element.height() : 0,
			radius: 3 * (params.zoom || 100) / 100,
			fill: 'white',
			stroke: 'black',
			strokeWidth: 1,
			draggable: true,
			visible: false
		});
	}

	drawBlockTitle(params: canvasBlockParams, canvasBlock: Konva.Group): void {
		canvasBlock.find('.title').forEach((node: Konva.Node) => node.destroy());

		const settings: PersonalSettings = this.sessionService.personalSettings,
			offset: number = 5,
			wrap: string = settings.blockNameWrap ? 'char' : 'none',
			fontCoefficient: { [key in BlockFontSizeType]: number; } = {
				small: 0.04,
				medium: 0.045,
				large: 0.05
			},
			fontSize: number = params.scale * fontCoefficient[settings.blockFontSize];

		if (canvasBlock.height() >= fontSize + offset * 2) {
			const titleGroup: Konva.Group = new Konva.Group({ draggable: false, name: 'title' }),
				blockName: Konva.Text = new Konva.Text({
					x: offset,
					y: offset,
					name: 'name',
					text: params.block.name,
					fontSize: fontSize,
					fontFamily: 'Roboto Condensed',
					wrap: wrap,
					ellipsis: true,
					width: canvasBlock.width() - offset * 2,
					listening: false
				}),
				blockTitleRect: Konva.Rect = new Konva.Rect({
					x: blockName.x() - offset,
					y: blockName.y() - offset,
					opacity: 0.7,
					listening: false
				});

			if (params.block.name || params.block.description) {
				blockTitleRect.height(blockName.getHeight() + offset * 2);
				blockTitleRect.width(blockName.textWidth + offset * 2);
			} else {
				blockName.height(0);
			}

			titleGroup.add(blockTitleRect);
			titleGroup.add(blockName);

			if (blockName.height() > canvasBlock.height() - offset * 2) {
				blockName.height(canvasBlock.height() - offset * 2);
				blockTitleRect.height(blockName.getHeight() + offset * 2);
			}

			if (params.block.image) {
				blockTitleRect.fill('#FFFFFF');
			}

			if (params.block.description) {
				blockName.width(blockName.width() - fontSize);
				const image: HTMLImageElement = new Image();
				image.src = '/static/img/icons/description.svg';
				const descriptionIcon: Konva.Image = new Konva.Image({
					name: 'description-icon',
					x: blockName.textWidth + offset,
					y: offset,
					height: fontSize,
					width: fontSize,
					image: image
				});
				blockTitleRect.width(descriptionIcon.x() + descriptionIcon.getWidth() + offset);
				titleGroup.add(descriptionIcon);
			}

			if (settings.blockPercentsShow && canvasBlock.height() >= fontSize * 2 + offset * 3) {
				const blockPercent: Konva.Text = new Konva.Text({
					name: 'percent',
					text: String(params.block.percent || 0) + '%',
					fontSize: fontSize,
					fontFamily: 'Roboto Condensed',
					listening: false
				});

				if (blockPercent.width() > canvasBlock.width() - offset * 2) {
					blockPercent.destroy();
				} else {
					titleGroup.add(blockPercent);

					if (blockName.height() > canvasBlock.height() - blockPercent.height() - offset * 3) {
						blockName.height(canvasBlock.height() - blockPercent.height() - offset * 3);
					}

					blockPercent.x(offset);
					blockPercent.y(blockName.height() + offset * 2);

					if (!params.detailedElement) {
						blockTitleRect.height(blockPercent.y() + blockPercent.getHeight() + offset);
					}
				}

				blockTitleRect.width(
					Math.max(
						blockTitleRect.x() + blockTitleRect.width(),
						blockPercent.x() + blockPercent.width() + offset
					)
				);
			}

			canvasBlock.add(titleGroup);
		}
	}

	drawBlockBorders(params: canvasBlockParams, canvasBlock: Konva.Group): void {
		canvasBlock.find('.borders').forEach((node: Konva.Node) => node.destroy());

		const bordersGroup: Konva.Group = new Konva.Group({ draggable: false, name: 'borders' }),
			borderPoints: Array<Array<number>> = [
				[0, 0, canvasBlock.width(), 0],
				[canvasBlock.width(), 0, canvasBlock.width(), canvasBlock.height()],
				[canvasBlock.width(), canvasBlock.height(), 0, canvasBlock.height()],
				[0, canvasBlock.height(), 0, 0]
			];

		borderPoints.forEach((border: Array<number>) => {
			const borderLine: Konva.Line = new Konva.Line({
				points: border,
				stroke: params.active ? 'red' : params.block.color.slice(0, 7),
				strokeWidth: 2,
				lineJoin: 'round',
				draggable: false
			});

			bordersGroup.add(borderLine);
		});

		canvasBlock.add(bordersGroup);
	}

	drawBlockSizeLines(params: canvasBlockParams, canvasBlock: Konva.Group): void {
		canvasBlock.find('.sizes').forEach((node: Konva.Node) => node.destroy());

		const thickness: number = 0.01 * params.scale,
			sizesGroup: Konva.Group = new Konva.Group({ draggable: false, name: 'sizes' }),
			sizesLines: Array<any> = [
				{
					x: canvasBlock.width() * 0.25,
					y: -(thickness / 2),
					width: canvasBlock.width() * 0.5,
					height: thickness,
					side: 'top'
				},
				{
					x: canvasBlock.width() - thickness / 2,
					y: canvasBlock.height() * 0.25,
					width: thickness,
					height: canvasBlock.height() * 0.5,
					side: 'right'
				},
				{
					x: canvasBlock.width() * 0.25,
					y: canvasBlock.height() - thickness / 2,
					width: canvasBlock.width() * 0.5,
					height: thickness,
					side: 'bottom'
				},
				{
					x: -(thickness / 2),
					y: canvasBlock.height() * 0.25,
					width: thickness,
					height: canvasBlock.height() * 0.5,
					side: 'left'
				}
			];

		sizesLines.forEach((line: any, index: number) => {
			const sizeLineArea = new Konva.Rect({
					id: line.side,
					name: 'size-area',
					x: index % 2 ? (line.x - 0.015 * params.scale) : line.x,
					y: index % 2 ? line.y : (line.y - 0.015 * params.scale),
					width: line.width + (index % 2 ? 0.03 * params.scale : 0),
					height: line.height + (index % 2 ? 0 : 0.03 * params.scale),
					draggable: params.draggable
				}),
				sizeLine = new Konva.Rect({
					x: line.x,
					y: line.y,
					width: line.width,
					height: line.height,
					fill: 'black',
					draggable: false
				});
			sizesGroup.add(sizeLine);
			sizesGroup.add(sizeLineArea);
		});

		if (params.active || params.selected) {
			sizesGroup.show();
		} else {
			sizesGroup.hide();
		}

		canvasBlock.add(sizesGroup);
	}

	changeBlockBordersStroke(block: Block, activeBlock: Block): void {
		const nodes: Array<Konva.Node> = this.getCanvasBlockBorders(block).getChildren();

		if (block == activeBlock) {
			nodes.forEach((node: Konva.Node) => node.setAttr('stroke', konvaSettings.activeColor));
			this.getCanvasBlockSizes(block).show();
		} else {
			nodes.forEach((node: Konva.Node) => node.setAttr('stroke', block.color.slice(0, 7)));
			this.getCanvasBlockSizes(block).hide();
		}
	}

	createCanvasBlock(params: canvasBlockParams): Konva.Group {
		if (this._blocks.has(params.block)) this._blocks.get(params.block)?.destroy();

		const block: Block = params.block
		const x: number = params.flip ? block.stand.Width - (block.begin[0].x + block.width) : block.begin[0].x;
		const blockGroup: Konva.Group = new Konva.Group({
				name: 'block',
				x: x * params.scale + params.beginPoint.x + konvaSettings.canvasOffset,
				y: block.begin[0].y * params.scale + params.beginPoint.y + konvaSettings.canvasOffset,
				width: block.width * params.scale,
				height: block.height * params.scale
			}),
			blockBody: Konva.Rect = new Konva.Rect({
				name: 'block-body',
				draggable: params.draggable,
				width: blockGroup.width(),
				height: blockGroup.height()
			});

		if (block.image) {
			const blockBodyImage: HTMLImageElement = new Image();
			blockBodyImage.crossOrigin = 'anonymous';
			blockBodyImage.src = block.image;
			blockBodyImage.onload = () => {
				blockBody.fillPatternImage(blockBodyImage);
				blockBody.fillPatternRepeat('no-repeat');
				blockBody.fillPatternScale({
					x: blockGroup.width() / blockBodyImage.width,
					y: blockGroup.height() / blockBodyImage.height
				});
			}
		} else {
			blockBody.fill(!block.hasIntersectionsWithProducts()
				? block.color
				: `#ff0000${block.color.slice(7, 9) || 'ff'}`
			);
		}

		blockGroup.add(blockBody);
		this.drawBlockTitle(params, blockGroup);
		this.drawBlockBorders(params, blockGroup);
		this.drawBlockSizeLines(params, blockGroup);

		this._blocks.set(block, blockGroup);

		if (params.selected) {
			this.getCanvasBlockBody(block).hide();
			this.getCanvasBlockTitle(block).hide();
		}

		return blockGroup;
	}

	getCanvasBlock(block: Block): Konva.Group {
		if (!this._blocks.has(block)) return this._defaultGroup;
		return this._blocks.get(block);
	}

	getCanvasBlockBody(block: Block): Konva.Rect {
		return this.getCanvasBlock(block).findOne('.block-body') || new Konva.Rect();
	}

	getCanvasBlockTitle(block: Block): Konva.Group {
		return this.getCanvasBlock(block).findOne('.title') || new Konva.Group();
	}

	getCanvasBlockName(block: Block): Konva.Text {
		return this.getCanvasBlock(block).findOne('.name') || new Konva.Text();
	}

	getCanvasBlockDescriptionIcon(block: Block): Konva.Image {
		return this.getCanvasBlock(block).findOne('.description-icon') || new Konva.Image({ image: new Image() });
	}

	getCanvasBlockTooltipNecessity(block: Block): boolean {
		const text: Konva.Text = this.getCanvasBlockName(block),
			originalName: string = text.text().replace(/ /g,''),
			truncatedName: string = text.textArr.map(v => v.text).join('').replace(/ /g,'');

		return originalName != truncatedName && !this.sessionService.personalSettings.blockNameWrap;
	}

	getCanvasBlockBorders(block: Block): Konva.Group {
		return this.getCanvasBlock(block).findOne('.borders') || new Konva.Group();
	}

	getCanvasBlockSizes(block: Block): Konva.Group {
		return this.getCanvasBlock(block).findOne('.sizes') || new Konva.Group();
	}

	createCanvasBlockDescriptionTooltip(block: Block, text: string, canvasElement: HTMLElement): Konva.Label {
		let wrappedText: string = '';
		let currentLine: string = '';

		text.split(' ').forEach((word: string) => {
			if ((currentLine + word).length > 50) {
				wrappedText += currentLine + '\n';
				currentLine = '';
			}
			currentLine += word + ' ';
		});
		wrappedText += currentLine;

		const tooltip = this.createCanvasTooltip(
			this.getCanvasBlockDescriptionIcon(block),
			wrappedText,
			canvasElement
		);
		if (this._blocksDescriptionsTooltips.has(block)) this._blocksDescriptionsTooltips.get(block)?.destroy();
		this._blocksDescriptionsTooltips.set(block, tooltip);
		return tooltip;
	}

	createCanvasBlockTooltip(block: Block, text: string, canvasElement: HTMLElement): Konva.Label {
		let wrappedText: string = '';
		let currentLine: string = '';

		text.split(' ').forEach((word: string) => {
			if ((currentLine + word).length > 50) {
				wrappedText += currentLine + '\n';
				currentLine = '';
			}
			currentLine += word + ' ';
		});
		wrappedText += currentLine;

		const tooltip = this.createCanvasTooltip(this.getCanvasBlockName(block), wrappedText, canvasElement);
		if (this._blocksTooltips.has(block)) this._blocksTooltips.get(block)?.destroy();
		this._blocksTooltips.set(block, tooltip);
		return tooltip;
	}

	getCanvasBlockTooltip(block: Block): Konva.Label {
		if (!this._blocksTooltips.has(block)) return new Konva.Label();
		return this._blocksTooltips.get(block);
	}

	createCanvasPOSMaterial(params: canvasPOSMaterialParams): Konva.Group {
		if (params.material instanceof POSTextMaterial) return this.createCanvasPOSTextMaterial(params);
		if (params.material instanceof POSImageMaterial) return this.createCanvasPOSImageMaterial(params);
	}

	createCanvasPOSImageMaterial(params: canvasPOSMaterialParams): Konva.Group {
		if (!(params.material instanceof POSImageMaterial)) return;

		if (this._POSMaterials.has(params.material)) this._POSMaterials.get(params.material)?.destroy();

		const material: POSImageMaterial = params.material;
		const materialGroup: Konva.Group = new Konva.Group({
			name: 'pos-image-material-group',
			draggable: params.draggable,
			x: material.x * params.scale + params.beginPoint.x + konvaSettings.canvasOffset,
			y: material.y * params.scale + params.beginPoint.y + konvaSettings.canvasOffset,
			width: material.width * params.scale,
			height: material.height * params.scale,
			rotation: material.angle
		});
		const materialBody: Konva.Rect = new Konva.Rect({
			name: 'pos-image-material-body',
			width: materialGroup.width(),
			height: materialGroup.height(),
			opacity: material.opacity
		});

		materialGroup.add(materialBody);

		const renderFallbackText = () => {
			materialBody.fill('#000000');
			const maxFontSize = 16;

			const materialText = new Konva.Text({
				name: 'pos-image-material-text',
				text: material.name,
				fill: '#FFFFFF',
				fontSize: maxFontSize
			});

			// Decrease fontSize until the text fits within the group width
			while (materialText.width() > materialGroup.width() && materialText.fontSize() > 1) {
				materialText.fontSize(materialText.fontSize() - 1);
			}

			materialText.x((materialGroup.width() - materialText.width()) / 2);
			materialText.y((materialGroup.height() - materialText.height()) / 2);

			materialGroup.add(materialText);
		}

		if (material.image) {
			const materialImage: HTMLImageElement = new Image();
			materialImage.crossOrigin = 'anonymous';
			materialImage.src = material.image;

			materialImage.onload = () => {
				materialBody.fill(null);
				materialBody.fillPatternImage(materialImage);
				materialBody.fillPatternRepeat('no-repeat');
				materialBody.fillPatternScale({
					x: materialGroup.width() / materialImage.width,
					y: materialGroup.height() / materialImage.height
				});
			};

			materialImage.onerror = () => renderFallbackText();
		} else {
			renderFallbackText();
		}

		if (material.description) {
			const iconSize: number = 12;
			const iconPadding: number = 1;

			const backgroundRect: Konva.Rect = new Konva.Rect({
				name: 'pos-image-material-description-bg',
				x: 0,
				y: 0,
				width: iconSize + iconPadding * 2,
				height: iconSize + iconPadding * 2,
				fill: '#FFFFFF',
				opacity: 0.7
			});

			const image: HTMLImageElement = new Image();
			image.src = '/static/img/icons/description.svg';
			const descriptionIcon: Konva.Image = new Konva.Image({
				name: 'pos-image-material-description-icon',
				x: iconPadding,
				y: iconPadding,
				height: iconSize,
				width: iconSize,
				image: image,
				listening: true,
				draggable: false
			});

			materialGroup.add(backgroundRect);
			materialGroup.add(descriptionIcon);

			const tooltip: Konva.Label = this.createCanvasPOSImageMaterialDescriptionTooltip(
				descriptionIcon,
				material.description,
				params.canvasElement
			);

			descriptionIcon.off('mouseenter mouseleave mousedown touchstart');

			descriptionIcon.on('mouseenter', () => {
				tooltip.show();
				tooltip.moveToTop();
			});

			descriptionIcon.on('mouseleave', () => {
				tooltip.hide();
			});

			descriptionIcon.on('mousedown touchstart', (e: KonvaEventObject<any>) => {
				e.cancelBubble = true;
			});

			params.tooltipGroup.add(tooltip);
		}

		if (params.draggable) {
			const borderRect = new Konva.Rect({
				name: 'pos-material-border',
				width: materialGroup.width(),
				height: materialGroup.height(),
				stroke: konvaSettings.activeColor,
				strokeWidth: 2,
				listening: false,
				visible: false
			});

			const rotationCircle = new Konva.Circle({
				name: 'pos-material-rotation-circle',
				x: materialGroup.width(),
				y: materialGroup.height(),
				radius: 3,
				fill: 'white',
				stroke: 'black',
				strokeWidth: 1,
				draggable: true,
				visible: false
			});

			materialGroup.add(borderRect);
			materialGroup.add(rotationCircle);
		}

		this._POSMaterials.set(material, materialGroup);

		if (params.active) this.setCanvasPOSMaterialActive(material);

		return materialGroup;
	}

	createCanvasPOSImageMaterialDescriptionTooltip(node: Konva.Node, text: string, canvasElement: HTMLElement): Konva.Label {
		let wrappedText: string = '';
		let currentLine: string = '';

		text.split(' ').forEach((word: string) => {
			if ((currentLine + word).length > 50) {
				wrappedText += currentLine + '\n';
				currentLine = '';
			}
			currentLine += word + ' ';
		});
		wrappedText += currentLine;

		return this.createCanvasTooltip(
			node,
			wrappedText,
			canvasElement
		);
	}

	createCanvasPOSTextMaterial(params: canvasPOSMaterialParams): Konva.Group {
		if (!(params.material instanceof POSTextMaterial)) return;

		if (this._POSMaterials.has(params.material)) this._POSMaterials.get(params.material)?.destroy();

		const material: POSTextMaterial = params.material;

		const maxWidth: number = material.width * params.scale;
		const initialFontSize: number = Math.round((material.fontSize * params.scale) / 400);
		const canvasCtx: CanvasRenderingContext2D = document.createElement('canvas').getContext('2d')!;
		let fittingFontSize: number = initialFontSize;

		canvasCtx.font = `${fittingFontSize}px sans-serif`;
		while (fittingFontSize > 4 && ![...material.name].every(char => canvasCtx.measureText(char).width + 4 <= maxWidth)) {
			fittingFontSize--;
			canvasCtx.font = `${fittingFontSize}px sans-serif`;
		}

		const materialGroup: Konva.Group = new Konva.Group({
				name: 'pos-text-material-group',
				draggable: params.draggable,
				x: material.x * params.scale + params.beginPoint.x + konvaSettings.canvasOffset,
				y: material.y * params.scale + params.beginPoint.y + konvaSettings.canvasOffset,
				rotation: material.angle
			}),
			materialText: Konva.Text = new Konva.Text({
				name: 'pos-text-material-text',
				padding: 2,
				text: material.name,
				fontSize: fittingFontSize,
				fill: material.color,
				wrap: 'word'
			});

		if (materialText.width() > material.width * params.scale) {
			materialText.width(material.width * params.scale);
		}

		materialGroup.width(materialText.width());
		materialGroup.height(materialText.height());

		materialGroup.add(materialText);

		if (params.draggable) {
			const borderRect = new Konva.Rect({
				name: 'pos-material-border',
				width: materialText.width(),
				height: materialText.height(),
				stroke: konvaSettings.activeColor,
				strokeWidth: 2,
				listening: false,
				visible: false
			});

			const rotationCircle = new Konva.Circle({
				name: 'pos-material-rotation-circle',
				x: materialText.width(),
				y: materialText.height(),
				radius: 3,
				fill: 'white',
				stroke: 'black',
				strokeWidth: 1,
				draggable: true,
				visible: false
			});

			materialGroup.add(borderRect);
			materialGroup.add(rotationCircle);
		}

		this._POSMaterials.set(material, materialGroup);
		if (params.active) this.setCanvasPOSMaterialActive(material);

		return materialGroup;
	}

	setCanvasPOSMaterialActive(material: POSMaterial): void {
		if (!this._POSMaterials.has(material)) return;
		const labelGroup: Konva.Group = this._POSMaterials.get(material);
		labelGroup.find('.pos-material-border, .pos-material-rotation-circle').forEach(node => node.visible(true));
	}

	getCanvasPOSMaterial(material: POSMaterial): Konva.Group {
		return this._POSMaterials.get(material);
	}

	setCanvasPOSMaterialInactive(material: POSMaterial): void {
		if (!this._POSMaterials.has(material)) return;
		const labelGroup: Konva.Group = this._POSMaterials.get(material);
		labelGroup.find('.pos-material-border, .pos-material-rotation-circle').forEach(node => node.visible(false));
	}

	createCanvasElementLabel(text: string, fill: string = konvaSettings.fillWhiteColor): Konva.Group {
		const labelGroup: Konva.Group = new Konva.Group({
				name: 'label'
			}),
			labelText: Konva.Text = new Konva.Text({
				text: text,
				fontSize: 12,
				x: 3,
				y: 2
			}),
			labelRect: Konva.Rect = new Konva.Rect({
				width: labelText.getWidth() + 6,
				height: labelText.getHeight() + 4,
				stroke: 'black',
				strokeWidth: 1,
				fill: fill
			});

		labelGroup.add(labelRect);
		labelGroup.add(labelText);
		labelGroup.setAttrs({ width: labelRect.width(), height: labelRect.height() });

		return labelGroup;
	}

	createCanvasEquipmentElement(params: canvasElementParams, equipment: Equipment): Konva.Group {
		params.element.shapes.forEach((shape: Figure) => (shape as any).representation = 'front');
		if (this._equipmentElements.has(params.element)) this._equipmentElements.get(params.element)?.destroy();

		const drawer: IElementDrawer = ElementDrawerFactory.getDrawer(params.element)
			.setBeginPoint(params.beginPoint)
			.setEquipment(equipment)
			.setOffset(konvaSettings.canvasOffset)
			.setScale(params.scale)
			.setShowLabels(params.showLabels !== false)
			.setState(params.flip);
		const elementGroup: Konva.Group = drawer.draw(params.active);

		this._equipmentElements.set(params.element, elementGroup);

		return elementGroup;
	}

	createCanvasEquipmentElementTooltip(params: canvasElementParams, equipment: Equipment, labels :ITooltipLabels): Konva.Label {
		if (this._equipmentElementTooltips.has(params.element)) this._equipmentElementTooltips.get(params.element)?.destroy();

		const drawer: IElementDrawer = ElementDrawerFactory.getDrawer(params.element)
			.setBeginPoint(params.beginPoint)
			.setEquipment(equipment)
			.setOffset(konvaSettings.canvasOffset)
			.setScale(params.scale)
			.setState(params.flip);
		const tooltip: Konva.Label = drawer.drawTooltip(labels, this._equipmentElements.get(params.element));

		this._equipmentElementTooltips.set(params.element, tooltip);

		return tooltip;
	}

	createEquipmentDistanceLines(params: canvasElementParams, equipment: Equipment, detailing: boolean, delta: Point): Array<Konva.Line | Konva.Label> {
		let x: number = null, y: number = null, z: number = null;

		if (!detailing) {
			z = params.element.shapes[0].begin.z;
		} else {
			y = params.element.getBoundingY().min;
		}

		const shape: Figure = params.element.shapes[0],
			elementShapes = Utils
				.to1DArray(
					equipment.allElements
						.filter((el: Element) => !el.parent.is(EquipmentElements.Hole))
						.map((el: Element) => el.shapes)
				)
				.filter((sh: Figure) => (sh.isPointOn(x, y, z) && sh != shape) || params.element.depend?.shapes.includes(sh)),
			nearShapesLines = elementShapes
				.map((s: Figure) => s.getLines(detailing ? 'top' : 'front', equipment))
				.flat(1),
			shapesLines: Array<Array<number>> = shape.getLines(detailing ? 'top' : 'front', equipment);

		let offsetLines: Array<IOffsetLine> = [
			{
				begin: {
					x: (shapesLines[0][0] + shapesLines[0][2]) / 2,
					y: (shapesLines[0][1] + shapesLines[0][3]) / 2
				},
				angle: Math.PI / -2,
				draw: false,
				distance: 200
			},
			{
				begin: {
					x: (shapesLines[1][0] + shapesLines[1][2]) / 2,
					y: (shapesLines[1][1] + shapesLines[1][3]) / 2
				},
				angle: 0,
				draw: false,
				distance: 200
			},
			{
				begin: {
					x: (shapesLines[2][0] + shapesLines[2][2]) / 2,
					y: (shapesLines[2][1] + shapesLines[2][3]) / 2
				},
				angle: Math.PI / 2,
				draw: false,
				distance: 200
			},
			{
				begin: {
					x: (shapesLines[3][0] + shapesLines[3][2]) / 2,
					y: (shapesLines[3][1] + shapesLines[3][3]) / 2
				},
				angle: Math.PI,
				draw: false,
				distance: 200
			}
		];

		offsetLines.forEach((line) => {
			if (delta.x != null && delta.y != null) {
				if (params.flip) {
					line.begin = {
						x: line.begin.x - delta.x / params.scale,
						y: line.begin.y + delta.y / params.scale
					};
				} else {
					line.begin = {
						x: line.begin.x + delta.x / params.scale,
						y: line.begin.y + delta.y / params.scale
					};
				}
			}

			const { x, y } = line.begin;

			line.end = {
				x: x + 200 * Math.cos(line.angle),
				y: y + 200 * Math.sin(line.angle)
			};
		});
		nearShapesLines.forEach((line) => {
			const lineString = GEOMETRY.createLineString([
				new jsts.geom.Coordinate(line[0], line[1]),
				new jsts.geom.Coordinate(line[2], line[3])
			]);

			offsetLines.forEach((offsetLine) => {
				const offsetString = GEOMETRY.createLineString([
					new jsts.geom.Coordinate(offsetLine.begin.x, offsetLine.begin.y),
					new jsts.geom.Coordinate(offsetLine.end.x, offsetLine.end.y)
				]);

				if (offsetString.intersects(lineString)) {
					const point = offsetString
							.intersection(lineString)
							.getCoordinates()[0],
						distance = GEOMETRY
							.createPoint(new jsts.geom.Coordinate(point.x, point.y))
							.distance(
								GEOMETRY.createPoint(
									new jsts.geom.Coordinate(offsetLine.begin.x, offsetLine.begin.y)
								)
							);

					if (distance < offsetLine.distance) {
						offsetLine.distance = distance;
						offsetLine.end = {
							x: point.x,
							y: point.y
						};
						offsetLine.draw = true;
					}
				}
			});
		});

		offsetLines = offsetLines.filter((line) => line.draw && line.distance > 0);

		const canvasGroups: Array<Konva.Line | Konva.Label> = [];
		const canvasOffset: number = detailing ? konvaSettings.detailedCanvasOffset : konvaSettings.canvasOffset;
		offsetLines.forEach((line) => {
			if (params.flip) {
				line.begin.x = equipment.Width - line.begin.x;
				line.end.x = equipment.Width - line.end.x;
			}

			canvasGroups.push(
				new Konva.Line({
					points: [
						(line.begin.x - params.normalize.x) * params.scale + canvasOffset + params.beginPoint.x,
						(line.begin.y - params.normalize.y) * params.scale + canvasOffset + params.beginPoint.y,
						(line.end.x - params.normalize.x) * params.scale + canvasOffset + params.beginPoint.x,
						(line.end.y - params.normalize.y) * params.scale + canvasOffset + params.beginPoint.y
					],
					strokeWidth: 1,
					stroke: 'black',
					dash: [5, 3]
				})
			);

			const text: string = String(UnitConverter.getConvertValue(line.distance, 'distance', 'inches', 3)),
				centerX: number = ((line.begin.x + line.end.x) / 2 - params.normalize.x) * params.scale,
				centerY: number = ((line.begin.y + line.end.y) / 2 - params.normalize.y) * params.scale,
				label: Konva.Label = new Konva.Label({
					x: centerX + canvasOffset + params.beginPoint.x,
					y: centerY + canvasOffset + params.beginPoint.y
				});

			label.add(new Konva.Text({ text }));
			label.offsetX(label.width() / 2);
			label.offsetY(([0, Math.PI].includes(line.angle) ? -1 : 1) * label.height() / 2);

			canvasGroups.push(label);
		});

		return canvasGroups;
	}

	createPlanogramSizes(params: canvasElementParams, equipment: Equipment): Array<Konva.Group> {
		const sizeGroups: Array<Konva.Group> = [];

		equipment.availableElements
			.filter((element: Element) => {
				return !['Hook', 'Hole', 'Box', 'SlicedBox', 'SidesWall', 'Partition'].includes(element.parent.type);
			})
			.forEach((element: Element) => {
				element.shapes.forEach((shape) => {
					const lines = shape.getLines('front', equipment);
					const bLine = lines[2];

					let text = String(UnitConverter.getConvertValue(
						Utils.distance(bLine[0], bLine[1], bLine[2], bLine[3]), 'distance', 'inches', 3
					));
					let center = Utils.center(bLine[0], bLine[1], bLine[2], bLine[3]);

					if (params.flip) center.x = equipment.Width - center.x;

					const bLabelGroup = new Konva.Group({});
					const lLabelGroup = new Konva.Group({});
					const bText = new Konva.Text({
						text: text,
						fontSize: Math.min((params.zoom - 100) / 10 + 10, MAX_SCALE_VALUE),
						offsetY: -3,
						offsetX: -2
					});
					const bRect = new Konva.Rect({
						width: bText.getWidth() + 5,
						height: bText.getHeight() + 5,
						stroke: 'black',
						strokeWidth: 1,
						fill: 'white'
					});
					const delta = shape.constructor.name == 'Trapeze' || element.depend?.shapes[0].constructor.name == 'Trapeze'
						? bRect.width() / params.scale / 2
						: 0;

					bLabelGroup.add(bRect);
					bLabelGroup.add(bText);

					bLabelGroup.setAttrs({
						x: (center.x - delta) * params.scale + konvaSettings.canvasOffset + params.beginPoint.x,
						y: center.y * params.scale + konvaSettings.canvasOffset + params.beginPoint.y
					});

					if (element.parent.type.includes('Base')) {
						bLabelGroup.y(bLabelGroup.getAttr('y') - element.Height * params.scale);
					}
					if (!['BackWall', 'HookWall', 'PerforationWall', 'Showcase'].includes(element.parent.type)) {
						sizeGroups.push(bLabelGroup);
					}

					const lLine = lines[1];

					text = String(UnitConverter.getConvertValue(
						shape.begin.y + (shape as any).height, 'distance', 'inches', 3
					));

					const lText = new Konva.Text({
						text: text,
						fontSize: Math.min((params.zoom - 100) / 10 + 10, MAX_SCALE_VALUE),
						offsetY: -3,
						offsetX: -2
					});
					const lRect = new Konva.Rect({
						width: lText.getWidth() + 5,
						height: lText.getHeight() + 5,
						stroke: 'black',
						strokeWidth: 1,
						fill: 'white'
					});

					lLabelGroup.add(lRect);
					lLabelGroup.add(lText);

					let startPointX: number = shape.begin.x;
					let startPointY: number = Math.max(lLine[1], lLine[3]) - (shape as any).height;

					if (params.flip) startPointX = equipment.Width - element.Width - shape.begin.x;

					lLabelGroup.setAttrs({
						x: startPointX * params.scale + konvaSettings.canvasOffset + params.beginPoint.x + params.scale / 9,
						y: startPointY * params.scale + konvaSettings.canvasOffset + params.beginPoint.y - lRect.height() / 2
					});

					sizeGroups.push(lLabelGroup);
				});
			});

		let shelves = equipment.availableElements
			.filter((el) => ['Shelf', 'AngleShelf'].includes(el.parent.type))
			.sort((a, b) => a.getBoundingX().max - b.getBoundingX().max);

		Utils.groupBy(shelves, 'depend').forEach((s) => {
			let distances = [];
			const standShelves = [...s, s[0].depend.depend].sort((a, b) => a.getBoundingY().max - b.getBoundingY().max);
			const commonBegin = Utils.common(standShelves.map((s) => (s.depend || s).getBoundingX().min));

			for (let i = 0; i < standShelves.length - 1; i++) {
				const d = {
					from: { x: commonBegin, y: equipment.Height - standShelves[i].getBoundingY().max },
					to: { x: commonBegin, y: equipment.Height - standShelves[i + 1].getBoundingY().min }
				};
				if (d.from.y - d.to.y > 0) distances.push(d);
			}
			distances.forEach((distance) => {
				const lLabelGroup = new Konva.Group({});
				let center = Utils.center(distance.from.x, distance.from.y, distance.to.x, distance.to.y);

				if (params.flip) center.x = equipment.Width - s[0].Width - center.x;

				const lText = new Konva.Text({
					text: String(UnitConverter.getConvertValue(
						Utils.distance(distance.from.x, distance.from.y, distance.to.x, distance.to.y), 'distance', 'inches', 3
					)),
					fontSize: Math.min((params.zoom - 100) / 10 + 10, MAX_SCALE_VALUE),
					offsetY: -3,
					offsetX: -2
				});
				const lRect = new Konva.Rect({
					width: lText.getWidth() + 5,
					height: lText.getHeight() + 5,
					stroke: 'black',
					strokeWidth: 1,
					fill: 'white'
				});

				lLabelGroup.add(lRect);
				lLabelGroup.add(lText);
				lLabelGroup.setAttrs({
					x: center.x * params.scale + params.beginPoint.x + konvaSettings.canvasOffset - lRect.height() / 2,
					y: center.y * params.scale + params.beginPoint.y + konvaSettings.canvasOffset + lRect.width() / 2,
					rotation: -90
				});

				sizeGroups.push(lLabelGroup);
			});
		});

		return sizeGroups;
	}

	getCanvasEquipmentElement(element: Element): Konva.Group {
		if (!this._equipmentElements.has(element)) return this._defaultGroup;
		return this._equipmentElements.get(element);
	}

	setEquipmentElementActive(element: Element): void {
		const canvasElement: Konva.Group = this.getCanvasEquipmentElement(element);

		canvasElement.find('.active, .stroke-active')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.activeColor));
		canvasElement.find('.active, .fill-active')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.activeColor));
	}

	setEquipmentElementInactive(element: Element): void {
		const canvasElement: Konva.Group = this.getCanvasEquipmentElement(element);

		canvasElement.find('.light, .stroke-light')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.fillGreyColor));
		canvasElement.find('.dark, .stroke-dark')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.fillDarkColor));
		canvasElement.find('.light, .fill-light')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillGreyColor));
		canvasElement.find('.dark, .fill-dark')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillDarkColor));
		canvasElement.find('.disabled')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillGreyColor));
	}

	setEquipmentElementHovered(element: Element): void {
		const canvasElement: Konva.Group = this.getCanvasEquipmentElement(element);

		canvasElement.find('.fill-hover-dark')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillHoverColor));
		canvasElement.find('.fill-hover-light')
			.forEach((shape: Konva.Shape) => shape.fill(konvaSettings.fillHoverColor));
		canvasElement.find('.stroke-hover-dark')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.fillDarkColor));
		canvasElement.find('.stroke-hover-light')
			.forEach((shape: Konva.Shape) => shape.stroke(konvaSettings.fillHoverColor));
	}

	// TODO merge with setCorrectFrontOrder
	setCorrectEquipmentOrder(equipment: Equipment, flip: boolean = false): void {
		const priority: { [key in EquipmentElements]: number } = {
			[EquipmentElements.BaseRectangle]: 1,
			[EquipmentElements.BaseTrapeze]: 1,
			[EquipmentElements.BackWall]: 2,
			[EquipmentElements.CentralPillar]: 2,
			[EquipmentElements.HookWall]: 2,
			[EquipmentElements.PerforatedWall]: 2,
			[EquipmentElements.SideWalls]: 9,
			[EquipmentElements.Showcase]: 10,
			[EquipmentElements.HookPanel]: 3,
			[EquipmentElements.Perforation]: 3,
			[EquipmentElements.Shelf]: 4,
			[EquipmentElements.AngleShelf]: 4,
			[EquipmentElements.HangBar]: 5,
			[EquipmentElements.Hook]: 8,
			[EquipmentElements.Hole]: 8,
			[EquipmentElements.Box]: 6,
			[EquipmentElements.SlicedBox]: 6,
			[EquipmentElements.Partition]: 7
		};

		if (!flip) {
			this.setEquipmentFrontSort(equipment, priority);
		} else {
			this.setEquipmentBackSort(equipment, priority);
		}
	}

	setEquipmentBackSort(equipment: Equipment, priority: { [key in EquipmentElements]: number }): void {
		const transformed: Array<any> = equipment.allElements
			.filter((element: Element) => !element.parent.is(EquipmentElements.Hole))
			.sort((a: Element, b: Element) => b.shapes[0].begin.x - a.shapes[0].begin.x || a.shapes[0].begin.y - b.shapes[0].begin.y)
			.map((el: Element) => {
				let zPoint: number = el.getBoundingZ().min;

				const trapezeSideWalls: boolean = el.parent.is(EquipmentElements.SideWalls) &&
					el.depend.parent.is(EquipmentElements.BaseTrapeze);

				if (trapezeSideWalls && el.depend.shapes[0].frontWidth < el.depend.shapes[0].backWidth) {
					zPoint = el.getBoundingZ().max;
				}

				if (trapezeSideWalls && el.depend.shapes[0].frontWidth > el.depend.shapes[0].backWidth) {
					const backWall = equipment.allElements
						.filter(e => e.parent.is(
							EquipmentElements.BackWall,
							EquipmentElements.HookWall,
							EquipmentElements.PerforatedWall
						))
						.find(e => e.getDepends().includes(el.depend));

					zPoint = backWall?.getBoundingZ().max ?? el.getBoundingZ().min;
				}

				if (el.parent.is(EquipmentElements.Showcase)) zPoint = el.getBoundingZ().max;

				return {
					zPoint: zPoint,
					priority: priority[el.parent.type],
					element: el,
					group: this.getCanvasEquipmentElement(el)
				};
			});
		const angled: Array<any> = transformed
			.filter((item: any) => item.element.depend?.parent.is(EquipmentElements.AngleShelf))
			.sort((a, b) => b.zPoint - a.zPoint || b.priority - a.priority);

		transformed
			.filter((item: any) => !item.element.depend?.parent.is(EquipmentElements.AngleShelf))
			.sort((a, b) => b.zPoint - a.zPoint || b.priority - a.priority)
			.forEach((group) => {
				group.group.moveToTop();
				if (group.element.parent.is(EquipmentElements.AngleShelf)) {
					angled.forEach((child) => {
						if (child.element.depend == group.element) child.group.moveToTop();
					});
				}
			});
	}

	setEquipmentFrontSort(equipment: Equipment, priority: { [key in EquipmentElements]: number }): void {
		const transformed: Array<any> = equipment.allElements
			.filter((element: Element) => !element.parent.is(EquipmentElements.Hole))
			.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x || a.shapes[0].begin.y - b.shapes[0].begin.y)
			.map((el: Element) => {
				let zPoint: number = el.getBoundingZ().max;

				const trapezeSideWalls: boolean = el.parent.is(EquipmentElements.SideWalls) &&
					el.depend.parent.is(EquipmentElements.BaseTrapeze);

				if (trapezeSideWalls && el.depend.shapes[0].backWidth < el.depend.shapes[0].frontWidth) {
					const backWall = equipment.allElements
						.filter(e => e.parent.is(
							EquipmentElements.BackWall,
							EquipmentElements.HookWall,
							EquipmentElements.PerforatedWall
						))
						.find(e => e.getDepends().includes(el.depend));

					zPoint = backWall?.getBoundingZ().max || el.getBoundingZ().min;
				}

				if (el.parent.is(EquipmentElements.Showcase)) zPoint = el.getBoundingZ().min;

				return {
					zPoint: zPoint,
					priority: priority[el.parent.type],
					element: el,
					group: this.getCanvasEquipmentElement(el)
				};
			});
		const angled: Array<any> = transformed
			.filter((item: any) => item.element.depend?.parent.is(EquipmentElements.AngleShelf))
			.sort((a, b) => a.zPoint - b.zPoint || a.priority - b.priority);

		transformed
			.filter((item: any) => !item.element.depend?.parent.is(EquipmentElements.AngleShelf))
			.sort((a, b) => a.zPoint - b.zPoint || a.priority - b.priority)
			.forEach((group) => {
				group.group.moveToTop();
				if (group.element.parent.is(EquipmentElements.AngleShelf)) {
					angled.forEach((child) => {
						if (child.element.depend == group.element) child.group.moveToTop();
					});
				}
			});
	}

	setCorrectPlanogramOrder(equipment: EquipmentBase, flip: boolean = false): void {
		const priority: { [key in EquipmentElements]: number } = {
			[EquipmentElements.BaseRectangle]: 1,
			[EquipmentElements.BaseTrapeze]: 1,
			[EquipmentElements.BackWall]: 2,
			[EquipmentElements.CentralPillar]: 2,
			[EquipmentElements.HookWall]: 2,
			[EquipmentElements.PerforatedWall]: 2,
			[EquipmentElements.SideWalls]: 9,
			[EquipmentElements.Showcase]: 10,
			[EquipmentElements.HookPanel]: 3,
			[EquipmentElements.Perforation]: 3,
			[EquipmentElements.Shelf]: 4,
			[EquipmentElements.AngleShelf]: 4,
			[EquipmentElements.HangBar]: 5,
			[EquipmentElements.Hook]: 8,
			[EquipmentElements.Hole]: 8,
			[EquipmentElements.Box]: 6,
			[EquipmentElements.SlicedBox]: 6,
			[EquipmentElements.Partition]: 7
		};

		if (!flip) {
			this.setPlanogramFrontSort(equipment, priority);
		} else {
			this.setPlanogramBackSort(equipment, priority);
		}

		// get boxes and products in front of boxes
		const boxElements: Array<Element> = (equipment as any).orderedElements
				.filter((el: Element) => el.constructor.name == 'BoxElement')
				.sort((a: Element, b: Element) => a.shapes[0].begin.z - b.shapes[0].begin.z),
			dependsProducts: Array<ProductGroup> = Utils.to1DArray(
				boxElements.map((el: Element) =>
					equipment.products
						.filter((pg: ProductGroup) => pg.element == el.depend)
						.filter((pg: ProductGroup) => pg.getBoundingZ().max >= pg.element.getAbsoluteBoundingZ().max)
						.filter((pg: ProductGroup) => {
							const boxXLimits: Bounding = el.getBoundingX(),
								productXLimits: Bounding = pg.getBoundingX();

							return (
								(boxXLimits.min <= productXLimits.min && boxXLimits.max > productXLimits.min) ||
								(boxXLimits.min < productXLimits.max && boxXLimits.max >= productXLimits.max) ||
								(boxXLimits.min >= productXLimits.min && boxXLimits.max <= productXLimits.max)
							);
						})
				)
			);

		// sort and draw products with error over other products and boxes
		(equipment as any).orderedElements
			.map((el: Element) => el.products.filter((group: Array<ProductGroup>) => group.some((pg: ProductGroup) => pg.error)))
			.reduce((products: Array<ProductGroup>, el: Array<ProductGroup>) => [...products, ...el], [])
			.filter((group: Array<ProductGroup>) => {
				return group.some((pg: ProductGroup) => {
					if (!pg.error) return false;

					return (
						pg.errorTypes.includes(ProductErrors.ElementHeight) ||
						pg.errorTypes.includes(ProductErrors.ShelfLeftLimit) ||
						pg.errorTypes.includes(ProductErrors.ShelfRightLimit) ||
						pg.errorTypes.includes(ProductErrors.BoxLeftLimit) ||
						pg.errorTypes.includes(ProductErrors.BoxRightLimit) ||
						pg.errorTypes.includes(ProductErrors.BoxIntersection) ||
						pg.errorTypes.includes(ProductErrors.DependLimits) ||
						pg.errorTypes.includes(ProductErrors.DependDepth) ||
						pg.errorTypes.includes(ProductErrors.BoxDepth)
					);
				});
			})
			.sort((a: Array<ProductGroup>, b: Array<ProductGroup>) => b[0].begin.y - a[0].begin.y || a[0].begin.x - b[0].begin.x)
			.forEach((group: Array<ProductGroup>) => {
				group
					.sort((a: ProductGroup, b: ProductGroup) => a.getBoundingZ().max - b.getBoundingZ().max)
					.forEach((pg: ProductGroup) => this.getCanvasProduct(pg).moveToTop());

				if (group[0].element.constructor.name == 'BoxElement') {
					this.getCanvasEquipmentElement(group[0].element).moveToTop();
					dependsProducts
						.filter((pg: ProductGroup) => group[0].element.depend && pg.element == group[0].element.depend)
						.forEach((pg: ProductGroup) => this.getCanvasProduct(pg).moveToTop());
				}
			});
	}

	setPlanogramBackSort(equipment: EquipmentBase, priority: { [key in EquipmentElements]: number }): void {
		const transformed: Array<any> = [
			...equipment.allElements
				.filter((element: Element) => !element.parent.is(EquipmentElements.Hole))
				.sort((a: Element, b: Element) => b.shapes[0].begin.x - a.shapes[0].begin.x || a.shapes[0].begin.y - b.shapes[0].begin.y)
				.map((el: Element) => {
					let zPoint: number = el.getBoundingZ().min;

					const trapezeSideWalls: boolean = el.parent.is(EquipmentElements.SideWalls) &&
						el.depend.parent.is(EquipmentElements.BaseTrapeze);

					if (trapezeSideWalls && el.depend.shapes[0].frontWidth < el.depend.shapes[0].backWidth) {
						zPoint = el.getBoundingZ().max;
					}

					if (trapezeSideWalls && el.depend.shapes[0].frontWidth > el.depend.shapes[0].backWidth) {
						const backWall = equipment.allElements
							.filter(e => e.parent.is(
								EquipmentElements.BackWall,
								EquipmentElements.HookWall,
								EquipmentElements.PerforatedWall
							))
							.find(e => e.getDepends().includes(el.depend));

						zPoint = backWall?.getBoundingZ().max ?? el.getBoundingZ().min;
					}

					if (el.parent.is(EquipmentElements.Showcase)) zPoint = el.getBoundingZ().max;

					return {
						zPoint: zPoint,
						priority: priority[el.parent.type],
						type: 'element',
						element: el,
						group: this.getCanvasEquipmentElement(el)
					};
				}),
			...equipment.products
				.sort((a: ProductGroup, b: ProductGroup) => b.begin.x - a.begin.x || a.begin.y - b.begin.y)
				.map((pg: ProductGroup) => {
					let zPoint: number = pg.getBoundingZ().min;

					if (pg.element.parent.is(EquipmentElements.Hole)) zPoint = pg.element.shapes[0].begin.z;

					return {
						zPoint: zPoint,
						priority: 11,
						type: 'product',
						element: pg.element,
						group: this.getCanvasProduct(pg)
					};
				})
		];
		const angled: Array<any> = transformed
			.filter((item: any) => item.element.depend?.parent.is(EquipmentElements.AngleShelf)
				|| (item.type == 'product' && item.element.parent.is(EquipmentElements.AngleShelf)))
			.sort((a, b) => b.zPoint - a.zPoint || a.priority - b.priority);

		transformed
			.filter((item: any) => !item.element.depend?.parent.is(EquipmentElements.AngleShelf)
				&& !(item.type == 'product' && item.element.parent.is(EquipmentElements.AngleShelf)))
			.sort((a, b) => b.zPoint - a.zPoint || a.priority - b.priority)
			.forEach((group) => {
				group.group.moveToTop();
				if (group.element.parent.is(EquipmentElements.AngleShelf)) {
					angled.forEach((child) => {
						if (child.element.depend == group.element || child.element == group.element) child.group.moveToTop();
					});
				}
			});
	}

	setPlanogramFrontSort(equipment: EquipmentBase, priority: { [key in EquipmentElements]: number }): void {
		const transformed: Array<any> = [
			...equipment.allElements
				.filter((element: Element) => !element.parent.is(EquipmentElements.Hole))
				.sort((a: Element, b: Element) => a.shapes[0].begin.x - b.shapes[0].begin.x || a.shapes[0].begin.y - b.shapes[0].begin.y)
				.map((el: Element) => {
					let zPoint: number = el.getBoundingZ().max;

					const trapezeSideWalls: boolean = el.parent.is(EquipmentElements.SideWalls) &&
						el.depend.parent.is(EquipmentElements.BaseTrapeze);

					if (trapezeSideWalls && el.depend.shapes[0].backWidth < el.depend.shapes[0].frontWidth) {
						const backWall = equipment.allElements
							.filter(e => e.parent.is(EquipmentElements.BackWall, EquipmentElements.HookWall, EquipmentElements.PerforatedWall))
							.find(e => e.getDepends().includes(el.depend));

						zPoint = backWall?.getBoundingZ().max || el.getBoundingZ().min;
					}

					if (el.parent.is(EquipmentElements.Showcase)) zPoint = el.getBoundingZ().min;

					return {
						zPoint: zPoint,
						priority: priority[el.parent.type],
						type: 'element',
						element: el,
						group: this.getCanvasEquipmentElement(el)
					};
				}),
			...equipment.products
				.sort((a: ProductGroup, b: ProductGroup) => b.begin.x - a.begin.x || a.begin.y - b.begin.y)
				.map((pg: ProductGroup) => {
					let zPoint: number = pg.getBoundingZ().max;

					if (pg.element.parent.is(EquipmentElements.Hole)) zPoint = pg.element.shapes[0].begin.z;

					return {
						zPoint: zPoint,
						priority: 11,
						type: 'product',
						element: pg.element,
						group: this.getCanvasProduct(pg)
					};
				})
		];
		const angled: Array<any> = transformed
			.filter((item: any) => item.element.depend?.parent.is(EquipmentElements.AngleShelf)
				|| (item.type == 'product' && item.element.parent.is(EquipmentElements.AngleShelf)))
			.sort((a, b) => a.zPoint - b.zPoint || a.priority - b.priority);

		transformed
			.filter((item: any) => !item.element.depend?.parent.is(EquipmentElements.AngleShelf)
				&& !(item.type == 'product' && item.element.parent.is(EquipmentElements.AngleShelf)))
			.sort((a, b) => a.zPoint - b.zPoint || a.priority - b.priority)
			.forEach((group) => {
				group.group.moveToTop();
				if (group.element.parent.is(EquipmentElements.AngleShelf)) {
					angled.forEach((child) => {
						if (child.element.depend == group.element || child.element == group.element) child.group.moveToTop();
					});
				}
			});
	}
}
